import React, { useState } from 'react';
import { Box, TextField, Button, Typography, FormControl, InputLabel, styled } from '@mui/material';
import FormDialog from '../../../../../../../components/Dialog/FormDialog';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { uploadHPDDocument } from '../../../../../../../sevices/violationsApi';
import UploadIcon from '../../../../../../../styles/svg/upload-file.svg';


const UploadButton = styled(Button)(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  boxShadow: "none",
  flexDirection: "column",
  gap: "8px",
}));

// Create validation schema
const schema = yup.object({
  title: yup.string().required('Document title is required').max(45, 'Document title must be less than 45 characters'),
  file: yup.mixed().required('Document file is required')
});

const DocumentUploadModal = ({ open, handleClose, ticketId, refetch }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      file: null
    }
  });

  // Mock upload mutation - replace with actual API call
  const { mutate: uploadDocument, isPending } = useMutation({
    mutationFn: uploadHPDDocument,
    onSuccess: () => {
      toast.success("Document uploaded successfully");
      reset();
      setSelectedFile(null);
      handleClose();
      if (refetch) refetch();
    },
    onError: () => {
      toast.error("Failed to upload document");
    },
  });

  const onSubmit = (data) => {
    // Create form data for file upload
    const formData = new FormData();
    formData.append('description', data.title);
    formData.append('file', data.file);
    formData.append('ticket_id', ticketId);
    
    // Call the mutation with the form data
    uploadDocument({id: ticketId, data: formData});
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setValue('file', file);
    }
  };

  const handleCancel = () => {
    if (!isPending) {
      reset();
      setSelectedFile(null);
      handleClose();
    }
  };

  return (
    <FormDialog open={open} handleClose={handleCancel} title="Document Upload">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Document Title"
            placeholder="Enter Title"
            {...register("title")}
            error={!!errors.title}
            helperText={errors.title?.message}
            InputLabelProps={{ shrink: true }}
            disabled={isPending}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel shrink>Upload</InputLabel>
            <label htmlFor="upload-file">
              <input
                accept=".pdf"
                id="upload-file"
                type="file"
                {...register("file")}
                onChange={handleFileChange}
                style={{ display: "none" }}
                error={errors?.file ? true : false}
              />
              <UploadButton
                variant="outlined"
                component="span"
                className="upload-btn"
              >
                <img src={UploadIcon} alt="upload" />
                {selectedFile && selectedFile.name
                  ? selectedFile.name
                  : "Upload"}
              </UploadButton>
            </label>
          {errors?.file && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
              {errors.file.message}
            </Typography>
          )}
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isPending}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
            }}
            className="secondary-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending}
            sx={{
              borderRadius: "4px",
              backgroundColor: "#F97316",
              "&:hover": { backgroundColor: "#EA580C" },
              textTransform: "none",
            }}
          >
            {isPending ? "Uploading..." : "Submit"}
          </Button>
        </Box>
      </form>
    </FormDialog>
  );
};

export default DocumentUploadModal; 